import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sellerAnalyticsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, CurrencyDollar, ShoppingCart, Users, TrendUp,
  Star, ChartBar, Package, UserPlus, Repeat
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function SellerAnalyticsPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [chart, setChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ov, tp, cu] = await Promise.all([
        sellerAnalyticsAPI.overview(),
        sellerAnalyticsAPI.topProducts(),
        sellerAnalyticsAPI.customers()
      ]);
      setOverview(ov.data);
      setTopProducts(tp.data);
      setCustomers(cu.data);
    } catch (err) { console.error('Failed to load seller analytics:', err); }
    setLoading(false);
    loadChart();
  };

  const loadChart = useCallback(async () => {
    try {
      const { data } = await sellerAnalyticsAPI.revenueChart(period);
      setChart(data);
    } catch (err) { console.error('Failed to load revenue chart:', err); }
  }, [period]);

  useEffect(() => { loadChart(); }, [loadChart]);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
  const maxRev = Math.max(...chart.map(d => d.revenue), 1);

  return (
    <div className="min-h-screen bg-[#111111]" data-testid="seller-analytics-page">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" /></Link>
            <span className="text-[#666666] hidden sm:inline">/</span>
            <span className="text-white font-semibold text-sm hidden sm:inline">Seller Analytics</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/crm"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-sm">CRM</Button></Link>
            <Link to="/dashboard"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Dashboard</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {loading ? (
          <div className="text-center py-20 text-[#666666]">Loading analytics...</div>
        ) : (
          <>
            {/* Stat Cards */}
            {overview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {[
                  { label: 'Total Revenue', value: fmt(overview.total_revenue), icon: CurrencyDollar, color: '#22C55E' },
                  { label: 'Total Sales', value: overview.total_sales, icon: ShoppingCart, color: '#22C55E' },
                  { label: 'Customers', value: overview.unique_customers, icon: Users, color: '#22C55E' },
                  { label: 'Avg Order', value: fmt(overview.avg_order_value), icon: TrendUp, color: '#F59E0B' },
                  { label: 'Products', value: overview.product_count, icon: Package, color: '#22C55E' },
                  { label: 'Followers', value: overview.followers, icon: UserPlus, color: '#22C55E' },
                  { label: 'Repeat Buyers', value: overview.repeat_buyers, icon: Repeat, color: '#22C55E' },
                ].map(({ label, value, icon: Icon, color }) => (
                  <Card key={label} className="bg-[#1A1A1A] border-[#222222] rounded-lg">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                        <Icon className="h-5 w-5" style={{ color }} weight="duotone" />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#666666] font-semibold">{label}</p>
                        <p className="text-lg font-bold text-white">{value}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Revenue Chart */}
            <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg mb-8">
              <CardHeader className="flex flex-row items-center justify-between border-b border-[#222222] py-3 px-4">
                <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                  <ChartBar className="h-4 w-4 text-[#22C55E]" /> Revenue
                </CardTitle>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-28 h-8 bg-[#111111] border-[#333333] text-white text-xs rounded-lg" data-testid="period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                    <SelectItem value="7d">7 Days</SelectItem>
                    <SelectItem value="30d">30 Days</SelectItem>
                    <SelectItem value="90d">90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="p-4">
                {chart.length === 0 ? (
                  <div className="text-center py-12 text-[#666666] text-sm">No revenue data yet</div>
                ) : (
                  <div className="flex items-end gap-1 h-40" data-testid="revenue-chart">
                    {chart.map((d) => (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                        <div
                          className="w-full bg-[#22C55E] rounded-t-sm min-h-[2px] transition-all"
                          style={{ height: `${Math.max((d.revenue / maxRev) * 140, 2)}px` }}
                          title={`${d.date}: ${fmt(d.revenue)} (${d.count} sales)`}
                        />
                        {chart.length <= 14 && (
                          <span className="text-[8px] text-[#555555] -rotate-45 origin-top-left whitespace-nowrap">
                            {d.date.slice(5)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Top Products */}
              <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg">
                <CardHeader className="border-b border-[#222222] py-3 px-4">
                  <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                    <Star className="h-4 w-4 text-[#F59E0B]" /> Top Products
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {topProducts.length === 0 ? (
                    <div className="p-6 text-center text-[#666666] text-sm">No product sales yet</div>
                  ) : (
                    <div className="divide-y divide-[#222222]">
                      {topProducts.map((p, i) => (
                        <div key={p.product_id} className="flex items-center gap-3 px-4 py-3" data-testid={`top-product-${i}`}>
                          <span className="text-xs font-bold text-[#444444] w-5">{i + 1}</span>
                          {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{p.name}</p>
                            <p className="text-xs text-[#666666]">{p.total_sales} sales</p>
                          </div>
                          <span className="text-sm font-semibold text-[#22C55E]">{fmt(p.total_revenue)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Customers */}
              <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg">
                <CardHeader className="border-b border-[#222222] py-3 px-4">
                  <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#22C55E]" /> Top Customers
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {customers.length === 0 ? (
                    <div className="p-6 text-center text-[#666666] text-sm">No customers yet</div>
                  ) : (
                    <div className="divide-y divide-[#222222]">
                      {customers.slice(0, 8).map((c, i) => (
                        <div key={c.user_id} className="flex items-center gap-3 px-4 py-3" data-testid={`top-customer-${i}`}>
                          {c.avatar_url ? (
                            <img src={c.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#333333] flex items-center justify-center text-white text-xs font-bold">{c.name?.[0]}</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{c.name}</p>
                            <p className="text-xs text-[#666666]">{c.transaction_count} purchases</p>
                          </div>
                          <span className="text-sm font-semibold text-[#22C55E]">{fmt(c.total_spent)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
